import React, { useEffect, useRef, useState, memo } from 'react';
import { useSelector } from 'react-redux';
import { Box, TextField, Typography, IconButton, Button } from '@mui/material';
import { lightTheme, darkTheme } from '../../themes/themes';
import SendIcon from '@mui/icons-material/Send';

const ChatBox = memo(({ sessionId, messages, sendMessage, userId, isSupportStaff, cannedResponses }) => {
  const messagesEndRef = useRef(null);
  const sessions = useSelector((state) => state.support.sessions);
  const currentSession = sessions.find((s) => s._id === sessionId);
  const isLightTheme = useSelector((state) => state.theme.light);
  const theme = isLightTheme ? lightTheme : darkTheme;
  const [newMessage, setNewMessage] = useState('');

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return isNaN(date.getTime()) ? 'Not available' : date.toLocaleTimeString('en-US', { hour12: true });
  };

  const requesterUsername = isSupportStaff ? currentSession?.support_request_id?.user_id?.username : null;

  const handleSend = () => {
    if (newMessage.trim()) {
      sendMessage(sessionId, newMessage);
      setNewMessage('');
    }
  };

  const handleCannedResponse = (response) => {
    sendMessage(sessionId, response.message, true, response._id);
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: 'calc(100vh - 120px)',
        display: 'flex',
        flexDirection: 'column',
        border: `1px solid ${theme.palette.divider || '#e0e0e0'}`,
        borderRadius: '12px',
        bgcolor: theme.palette.background.paper,
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
        overflow: 'hidden',
      }}
    >
      {isSupportStaff && requesterUsername && (
        <Typography
          variant="subtitle2"
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            bgcolor: theme.palette.background.default,
            p: '4px 8px',
            borderRadius: '8px',
            fontSize: '0.85rem',
            color: theme.palette.text.secondary,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          }}
        >
          User: {requesterUsername}
        </Typography>
      )}

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          p: 2,
          pt: isSupportStaff ? 5 : 2,
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.text.secondary,
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: theme.palette.background.paper,
          },
        }}
      >
        {messages.map((msg) => {
          const senderId = msg.sender_id?._id || msg.sender_id.toString();
          const isCurrentUser = senderId === userId;

          return (
            <Box
              key={msg._id}
              sx={{
                display: 'flex',
                justifyContent: isCurrentUser ? 'flex-end' : 'flex-start',
                alignItems: 'flex-end',
                mb: 2,
              }}
            >
              <Box
                sx={{
                  p: '10px 14px',
                  bgcolor: isCurrentUser ? theme.palette.brand.main : theme.palette.background.default,
                  color: isCurrentUser ? '#fff' : theme.palette.text.primary,
                  borderRadius: '12px',
                  maxWidth: '70%',
                  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
                  textAlign: isCurrentUser ? 'right' : 'left',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.75rem',
                    opacity: 0.8,
                    display: 'block',
                    mb: 0.5,
                  }}
                >
                  {isCurrentUser ? 'You' : 'Support'}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    wordBreak: 'break-word',
                    fontSize: '1rem',
                    lineHeight: 1.4,
                  }}
                >
                  {msg.message}{' '}
                  {msg.is_canned && <small style={{ fontSize: '0.7rem', opacity: 0.7 }}>(Canned response)</small>}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.65rem',
                    display: 'block',
                    opacity: 0.6,
                    mt: 0.5,
                    textAlign: 'right',
                  }}
                >
                  {formatTimestamp(msg.timestamp || msg.created_at)}
                </Typography>
              </Box>
            </Box>
          );
        })}
        <div ref={messagesEndRef} />
      </Box>

      <Box
        sx={{
          p: 2,
          bgcolor: theme.palette.background.paper,
          borderTop: `1px solid ${theme.palette.divider || '#ddd'}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: isSupportStaff && cannedResponses.length > 0 ? 1 : 0 }}>
          <TextField
            fullWidth
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type your message..."
            size="small"
            sx={{
              bgcolor: theme.palette.background.paper,
              borderRadius: '6px',
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: theme.palette.divider || '#ddd' },
                '&:hover fieldset': { borderColor: theme.palette.text.secondary },
              },
              '& .MuiInputBase-input': {
                color: theme.palette.text.primary,
                pr: 6,
              },
            }}
            InputProps={{
              endAdornment: (
                <IconButton
                  onClick={handleSend}
                  sx={{
                    color: theme.palette.brand.main,
                    '&:hover': { color: '#d4a027' },
                  }}
                >
                  <SendIcon fontSize="small" />
                </IconButton>
              ),
            }}
          />
        </Box>

        {/* الردود المعلبة لموظفي الدعم فقط */}
        {isSupportStaff && cannedResponses.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {cannedResponses.map((response) => (
              <Button
                key={response._id}
                variant="outlined"
                size="small"
                onClick={() => handleCannedResponse(response)}
                sx={{
                  borderColor: theme.palette.brand.main,
                  color: theme.palette.brand.main,
                  '&:hover': {
                    borderColor: '#d4a027',
                    color: '#d4a027',
                  },
                  borderRadius: '6px',
                  fontSize: '0.75rem',
                  px: 2,
                  py: 0.5,
                }}
              >
                {response.message}
              </Button>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
});

ChatBox.displayName = 'ChatBox';

export default ChatBox;